<template>
  <div class="verification-page">
    <!-- 导航栏 -->
    <van-nav-bar
      :title="$t('verification.title')"
      left-arrow
      fixed
      placeholder
      safe-area-inset-top
      @click-left="$router.back()"
    />

    <!-- 主要内容 -->
    <div class="verification-content">
      <!-- 输入区域 -->
      <div class="input-section">
        <div class="section-header">
          <van-icon
            name="edit"
            class="section-icon"
          />
          <h2 class="section-title">
            {{ $t('verification.manualInput') }}
          </h2>
        </div>

        <van-field
          v-model="securityCode"
          :placeholder="$t('verification.enterCode')"
          clearable
          maxlength="50"
          :error-message="errorMessage"
          class="code-input"
          size="large"
        >
          <template #button>
            <van-button
              size="large"
              type="primary"
              :loading="verifying"
              :disabled="!securityCode.trim()"
              class="verify-btn"
              @click="handleManualVerification"
            >
              {{ $t('verification.verify') }}
            </van-button>
          </template>
        </van-field>

        <!-- 扫码区域 -->
        <div class="scan-section">
          <div class="section-header">
            <van-icon
              name="scan"
              class="section-icon"
            />
            <h2 class="section-title">
              {{ $t('verification.scanInput') }}
            </h2>
          </div>

          <van-button
            block
            type="primary"
            icon="scan"
            size="large"
            :loading="scanning"
            class="scan-btn"
            @click="startScan"
          >
            {{ $t('verification.scanCode') }}
          </van-button>

          <!-- 扫码提示 -->
          <div class="scan-tips">
            <van-icon name="info" />
            <span>{{ $t('verification.scanTip') }}</span>
          </div>
        </div>
      </div>

      <!-- 扫码界面 -->
      <div
        v-if="showScanner"
        class="scanner-section"
      >
        <div
          ref="scannerContainer"
          class="scanner-container"
        >
          <!-- 扫码界面将在这里动态创建 -->
        </div>

        <div class="scanner-overlay">
          <div class="scan-frame">
            <div class="frame-corner top-left" />
            <div class="frame-corner top-right" />
            <div class="frame-corner bottom-left" />
            <div class="frame-corner bottom-right" />
          </div>
          <p class="scan-guide">
            {{ $t('verification.scanGuide') }}
          </p>
        </div>

        <div class="scanner-actions">
          <van-button
            round
            type="default"
            icon="cross"
            size="large"
            class="close-scanner-btn"
            @click="stopScan"
          >
            {{ $t('common.close') }}
          </van-button>
        </div>
      </div>

      <!-- 查询结果 -->
      <div
        v-if="verificationResult"
        class="result-section"
      >
        <!-- 验证状态 -->
        <div
          class="status-card"
          :class="getStatusClass(verificationResult)"
        >
          <div class="status-icon">
            <van-icon :name="getStatusIcon(verificationResult)" />
          </div>
          <div class="status-content">
            <h3 class="status-title">
              {{ getStatusTitle(verificationResult) }}
            </h3>
            <p class="status-message">
              {{ verificationResult.message }}
            </p>
          </div>
        </div>

        <!-- 扫描信息 -->
        <div class="scan-info-card">
          <h4 class="card-title">
            {{ $t('verification.scanInfo') }}
          </h4>
          <div class="info-grid">
            <div class="info-item">
              <label>{{ $t('verification.isFirstScan') }}</label>
              <span :class="{ 'first-scan': verificationResult.isFirstScan }">
                {{ verificationResult.isFirstScan ? $t('verification.yes') : $t('verification.no') }}
              </span>
            </div>
            <div class="info-item">
              <label>{{ $t('verification.scanTimes') }}</label>
              <span class="scan-count">{{ verificationResult.scanTimes }}</span>
            </div>
            <div
              v-if="verificationResult.firstScanTime"
              class="info-item"
            >
              <label>{{ $t('verification.firstScanTime') }}</label>
              <span class="scan-time">{{ formatDate(verificationResult.firstScanTime) }}</span>
            </div>
            <div class="info-item">
              <label>{{ $t('verification.currentScanTime') }}</label>
              <span class="scan-time">{{ formatDate(new Date()) }}</span>
            </div>
          </div>
        </div>

        <!-- 商品信息 -->
        <div
          v-if="verificationResult.commodityInfo"
          class="commodity-card"
        >
          <h4 class="card-title">
            {{ $t('verification.commodityInfo') }}
          </h4>
          <div class="commodity-content">
            <!-- 商品图片 -->
            <div
              v-if="verificationResult.commodityInfo.stylePhoto"
              class="commodity-image"
            >
              <img
                :src="verificationResult.commodityInfo.stylePhoto"
                :alt="verificationResult.commodityInfo.styleDescription"
                @load="handleImageLoad"
                @error="handleImageError"
              >
            </div>

            <!-- 商品详情 -->
            <div class="commodity-details">
              <h5 class="commodity-name">
                {{ verificationResult.commodityInfo.styleDescription || $t('verification.unknownProduct') }}
              </h5>

              <div class="commodity-specs">
                <div
                  v-if="verificationResult.commodityInfo.styleNo"
                  class="spec-item"
                >
                  <van-icon
                    name="tag"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.styleNo') }}</label>
                    <span>{{ verificationResult.commodityInfo.styleNo }}</span>
                  </div>
                </div>
                <div
                  v-if="verificationResult.commodityInfo.sptColor"
                  class="spec-item"
                >
                  <van-icon
                    name="color"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.color') }}</label>
                    <span>{{ verificationResult.commodityInfo.sptColor }}</span>
                  </div>
                </div>
                <div
                  v-if="verificationResult.commodityInfo.sizeName"
                  class="spec-item"
                >
                  <van-icon
                    name="size"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.size') }}</label>
                    <span>{{ verificationResult.commodityInfo.sizeName }}</span>
                  </div>
                </div>
                <div
                  v-if="verificationResult.commodityInfo.stockType"
                  class="spec-item"
                >
                  <van-icon
                    name="bag"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.category') }}</label>
                    <span>{{ verificationResult.commodityInfo.stockType }}</span>
                  </div>
                </div>
                <div
                  v-if="verificationResult.commodityInfo.country"
                  class="spec-item"
                >
                  <van-icon
                    name="location"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.country') }}</label>
                    <span>{{ verificationResult.commodityInfo.country }}</span>
                  </div>
                </div>
                <div
                  v-if="verificationResult.commodityInfo.collectionNo"
                  class="spec-item"
                >
                  <van-icon
                    name="certificate"
                    class="spec-icon"
                  />
                  <div class="spec-content">
                    <label>{{ $t('verification.collection') }}</label>
                    <span>{{ verificationResult.commodityInfo.collectionNo }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <van-button
            type="primary"
            size="large"
            class="action-btn primary-btn"
            @click="handleVerifyAnother"
          >
            <van-icon name="replay" />
            {{ $t('verification.verifyAnother') }}
          </van-button>
          <van-button
            type="default"
            size="large"
            class="action-btn secondary-btn"
            @click="handleShareResult"
          >
            <van-icon name="share" />
            {{ $t('verification.shareResult') }}
          </van-button>
        </div>
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="hasSearched"
        class="empty-state"
      >
        <van-empty
          :description="$t('verification.noResult')"
          image="search"
        >
          <van-button
            round
            type="primary"
            size="large"
            @click="resetSearch"
          >
            {{ $t('verification.tryAgain') }}
          </van-button>
        </van-empty>
      </div>

      <!-- 使用说明 -->
      <div
        v-else
        class="instruction-section"
      >
        <div class="instruction-card">
          <h3 class="instruction-title">
            {{ $t('verification.howToUse') }}
          </h3>
          <div class="instruction-steps">
            <div class="step-item">
              <div class="step-number">
                1
              </div>
              <div class="step-content">
                <h4>{{ $t('verification.step1Title') }}</h4>
                <p>{{ $t('verification.step1Desc') }}</p>
              </div>
            </div>
            <div class="step-item">
              <div class="step-number">
                2
              </div>
              <div class="step-content">
                <h4>{{ $t('verification.step2Title') }}</h4>
                <p>{{ $t('verification.step2Desc') }}</p>
              </div>
            </div>
            <div class="step-item">
              <div class="step-number">
                3
              </div>
              <div class="step-content">
                <h4>{{ $t('verification.step3Title') }}</h4>
                <p>{{ $t('verification.step3Desc') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分享弹窗 -->
    <van-share-sheet
      v-model:show="showShareSheet"
      :options="shareOptions"
      :title="$t('verification.shareTitle')"
      @select="onShareSelect"
    />

    <!-- 加载遮罩 -->
    <van-overlay
      :show="verifying"
      class="verifying-overlay"
    >
      <div class="overlay-content">
        <van-loading
          size="32px"
          vertical
        >
          {{ $t('verification.verifying') }}
        </van-loading>
      </div>
    </van-overlay>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showToast } from 'vant'
import { getUserLang } from '@/storage/user'

const { t } = useI18n()
const router = useRouter()

// 响应式数据
const securityCode = ref('')
const errorMessage = ref('')
const verifying = ref(false)
const scanning = ref(false)
const showScanner = ref(false)
const hasSearched = ref(false)
const showShareSheet = ref(false)
const verificationResult = ref<any>(null)
const scannerContainer = ref<HTMLElement>()
let html5QrCode: any = null

// 模拟验证结果数据
const mockVerificationResult = {
  isFirstScan: true,
  firstScanTime: '2024-01-15T10:30:00Z',
  scanTimes: 1,
  message: '该产品为正品，请放心使用',
  commodityInfo: {
    id: 174,
    sptStyleInfoId: 137688,
    sptStyleColorId: 162973,
    sptClientLinkId: 281,
    sptBrandOfficialAccountsId: 34,
    qty: 15,
    clientLink: 'THE ANTI ORDER',
    collectionNo: '2022-Q3',
    styleNo: 'TAOJK100067',
    sptColor: 'Black/Navy/Red 黑色',
    sizeName: '3XL',
    stylePhoto: 'http://58.215.206.250:8008/static/img/StyleImg/款式图片_20250930171600691.jpg',
    stockType: '夹克',
    styleDescription: 'TOKYO MOTO RACING JACKET',
    country: '澳大利亚',
    imageList: []
  }
}

// 分享选项
const shareOptions = [
  { name: '微信', icon: 'wechat' },
  { name: '朋友圈', icon: 'wechat-moments' },
  { name: '微博', icon: 'weibo' },
  { name: 'QQ', icon: 'qq' },
  { name: '复制链接', icon: 'link' }
]

// 方法
const validateSecurityCode = (code: string): boolean => {
  if (!code.trim()) {
    errorMessage.value = t('verification.codeRequired')
    return false
  }

  if (code.trim().length < 8) {
    errorMessage.value = t('verification.codeTooShort')
    return false
  }

  errorMessage.value = ''
  return true
}

const handleManualVerification = async () => {
  if (!validateSecurityCode(securityCode.value)) {
    return
  }

  await verifySecurityCode(securityCode.value.trim())
}

const verifySecurityCode = async (code: string) => {
  verifying.value = true
  hasSearched.value = true

  try {
    // 模拟API调用
    await new Promise(resolve => setTimeout(resolve, 2000))

    // 模拟根据不同的防伪码返回不同结果
    if (code === '1234567890') {
      verificationResult.value = mockVerificationResult
    } else if (code === '0987654321') {
      verificationResult.value = {
        ...mockVerificationResult,
        isFirstScan: false,
        scanTimes: 5,
        firstScanTime: '2024-01-10T14:20:00Z',
        message: '该产品已验证多次，首次验证时间为2024-01-10'
      }
    } else {
      verificationResult.value = null
      showToast(t('verification.codeInvalid'))
    }
  } catch (error) {
    showToast(t('verification.verifyFailed'))
    verificationResult.value = null
  } finally {
    verifying.value = false
  }
}

const startScan = async () => {
  scanning.value = true

  try {
    // 动态导入html5-qrcode库
    const { Html5Qrcode } = await import('html5-qrcode')

    if (!scannerContainer.value) {
      throw new Error('Scanner container not found')
    }

    // 创建扫码器实例
    html5QrCode = new Html5Qrcode(scannerContainer.value.id)

    // 配置扫码器 - 针对1080×1920设备优化
    const config = {
      fps: 10,
      qrbox: { width: 300, height: 300 }, // 增大扫码框
      aspectRatio: 1.0
    }

    // 开始扫码
    await html5QrCode.start(
      { facingMode: 'environment' },
      config,
      onScanSuccess,
      onScanFailure
    )

    showScanner.value = true
    scanning.value = false
  } catch (error) {
    console.error('Failed to start scanner:', error)
    scanning.value = false

    // 降级方案：使用文件上传方式
    showToast(t('verification.cameraNotAvailable'))
  }
}

const onScanSuccess = (decodedText: string) => {
  // 停止扫码
  stopScan()

  // 将扫码结果填入输入框
  securityCode.value = decodedText

  // 自动开始验证
  handleManualVerification()

  showToast(t('verification.scanSuccess'))
}

const onScanFailure = (error: string) => {
  // 扫码失败的处理，通常是继续扫描
  console.log('Scan failed:', error)
}

const stopScan = () => {
  if (html5QrCode) {
    html5QrCode.stop().then(() => {
      html5QrCode.clear()
      html5QrCode = null
    }).catch((error: any) => {
      console.error('Failed to stop scanner:', error)
    })
  }

  showScanner.value = false
  scanning.value = false
}

const getStatusClass = (result: any) => {
  if (result.isFirstScan) {
    return 'status-first-scan'
  } else if (result.scanTimes <= 3) {
    return 'status-normal'
  } else {
    return 'status-multiple-scans'
  }
}

const getStatusIcon = (result: any) => {
  if (result.isFirstScan) {
    return 'certificate'
  } else {
    return 'shield'
  }
}

const getStatusTitle = (result: any) => {
  if (result.isFirstScan) {
    return t('verification.authenticProduct')
  } else {
    return t('verification.verifiedProduct')
  }
}

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString)
  const lang = getUserLang()
  return new Intl.DateTimeFormat(lang, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date)
}

const handleImageLoad = () => {
  // 图片加载成功
}

const handleImageError = () => {
  console.error('商品图片加载失败')
}

const handleVerifyAnother = () => {
  resetSearch()
}

const handleShareResult = () => {
  showShareSheet.value = true
}

const onShareSelect = (option: any) => {
  showShareSheet.value = false

  if (option.name === '复制链接') {
    navigator.clipboard.writeText(window.location.href)
    showToast(t('verification.linkCopied'))
  } else {
    showToast(`${t('verification.sharing')} ${option.name}`)
  }
}

const resetSearch = () => {
  securityCode.value = ''
  errorMessage.value = ''
  verificationResult.value = null
  hasSearched.value = false
}

// 生命周期
onMounted(() => {
  // 可以在这里添加初始化逻辑
})

onUnmounted(() => {
  // 清理扫码器
  if (html5QrCode) {
    stopScan()
  }
})
</script>

<style lang="scss" scoped>
.verification-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

.verification-content {
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
}

/* 通用区块样式 */
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .section-icon {
    font-size: 24px;
    color: #1989fa;
  }

  .section-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1a1a1a;
  }
}

/* 输入区域 */
.input-section {
  background: white;
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  .code-input {
    margin-bottom: 24px;

    :deep(.van-field__body) {
      font-size: 18px;
    }

    :deep(.van-field__button) {
      padding-left: 16px;
    }

    .verify-btn {
      height: 52px;
      border-radius: 26px;
      font-size: 18px;
      font-weight: 500;
      padding: 0 24px;
    }
  }

  .scan-section {
    .scan-btn {
      height: 60px;
      border-radius: 30px;
      font-size: 18px;
      font-weight: 500;
      margin-bottom: 16px;
    }

    .scan-tips {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      color: #999;

      .van-icon {
        margin-right: 8px;
        font-size: 16px;
      }
    }
  }
}

/* 扫码区域 */
.scanner-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  .scanner-container {
    flex: 1;
    position: relative;
  }

  .scanner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    .scan-frame {
      width: 300px;
      height: 300px;
      position: relative;
      margin-bottom: 32px;

      .frame-corner {
        position: absolute;
        width: 30px;
        height: 30px;
        border: 3px solid #00ff00;

        &.top-left {
          top: 0;
          left: 0;
          border-right: none;
          border-bottom: none;
        }

        &.top-right {
          top: 0;
          right: 0;
          border-left: none;
          border-bottom: none;
        }

        &.bottom-left {
          bottom: 0;
          left: 0;
          border-right: none;
          border-top: none;
        }

        &.bottom-right {
          bottom: 0;
          right: 0;
          border-left: none;
          border-top: none;
        }
      }
    }

    .scan-guide {
      color: white;
      font-size: 16px;
      text-align: center;
      margin: 0;
    }
  }

  .scanner-actions {
    padding: 24px;
    text-align: center;

    .close-scanner-btn {
      height: 52px;
      border-radius: 26px;
      font-size: 16px;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: none;
      padding: 0 32px;
    }
  }
}

/* 结果区域 */
.result-section {
  .status-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

    &.status-first-scan {
      border-left: 6px solid #07c160;
    }

    &.status-normal {
      border-left: 6px solid #1989fa;
    }

    &.status-multiple-scans {
      border-left: 6px solid #ff976a;
    }

    .status-icon {
      .van-icon {
        font-size: 48px;

        .status-first-scan & {
          color: #07c160;
        }

        .status-normal & {
          color: #1989fa;
        }

        .status-multiple-scans & {
          color: #ff976a;
        }
      }
    }

    .status-content {
      flex: 1;

      .status-title {
        margin: 0 0 12px;
        font-size: 24px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .status-message {
        margin: 0;
        font-size: 16px;
        color: #666;
        line-height: 1.5;
      }
    }
  }

  .scan-info-card,
  .commodity-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    margin-bottom: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

    .card-title {
      margin: 0 0 24px;
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
    }
  }

  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      label {
        font-size: 16px;
        color: #666;
        font-weight: 500;
      }

      span {
        font-size: 16px;
        font-weight: 500;
        color: #1a1a1a;

        &.first-scan {
          color: #07c160;
          font-weight: 600;
        }

        &.scan-count {
          color: #1989fa;
        }

        &.scan-time {
          color: #999;
          font-size: 15px;
        }
      }
    }
  }

  .commodity-content {
    display: flex;
    gap: 24px;

    .commodity-image {
      width: 160px;
      height: 160px;
      border-radius: 12px;
      overflow: hidden;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .commodity-details {
      flex: 1;
      min-width: 0;

      .commodity-name {
        margin: 0 0 20px;
        font-size: 20px;
        font-weight: 600;
        color: #1a1a1a;
        line-height: 1.3;
      }

      .commodity-specs {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .spec-item {
          display: flex;
          align-items: center;
          gap: 12px;

          .spec-icon {
            font-size: 20px;
            color: #1989fa;
            width: 24px;
            flex-shrink: 0;
          }

          .spec-content {
            flex: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;

            label {
              font-size: 14px;
              color: #999;
              font-weight: 500;
            }

            span {
              font-size: 14px;
              color: #333;
              font-weight: 500;
              text-align: right;
            }
          }
        }
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 16px;

    .action-btn {
      flex: 1;
      height: 60px;
      border-radius: 30px;
      font-size: 18px;
      font-weight: 500;

      .van-icon {
        margin-right: 8px;
      }

      &.primary-btn {
        background: linear-gradient(135deg, #1989fa 0%, #007aff 100%);
        border: none;
      }

      &.secondary-btn {
        border: 2px solid #dcdfe6;
        background: white;
        color: #606266;
      }
    }
  }
}

/* 使用说明 */
.instruction-section {
  .instruction-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

    .instruction-title {
      margin: 0 0 32px;
      font-size: 24px;
      font-weight: 600;
      color: #1a1a1a;
      text-align: center;
    }

    .instruction-steps {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .step-item {
      display: flex;
      align-items: flex-start;
      gap: 20px;

      .step-number {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: linear-gradient(135deg, #1989fa 0%, #007aff 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        font-weight: 600;
        flex-shrink: 0;
      }

      .step-content {
        flex: 1;

        h4 {
          margin: 0 0 8px;
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
        }

        p {
          margin: 0;
          font-size: 16px;
          color: #666;
          line-height: 1.5;
        }
      }
    }
  }
}

.empty-state {
  margin-top: 80px;
}

.verifying-overlay {
  display: flex;
  align-items: center;
  justify-content: center;

  .overlay-content {
    text-align: center;
    color: white;

    :deep(.van-loading__text) {
      font-size: 18px;
      margin-top: 16px;
    }
  }
}

/* 1080×1920 设备特别优化 */
@media (min-width: 1080px) and (min-height: 1920px) {
  .verification-content {
    padding: 32px;
    max-width: 700px;
  }

  .input-section {
    padding: 40px;
    margin-bottom: 32px;

    .code-input {
      margin-bottom: 32px;

      :deep(.van-field__body) {
        font-size: 20px;
      }

      .verify-btn {
        height: 60px;
        border-radius: 30px;
        font-size: 20px;
        padding: 0 32px;
      }
    }

    .scan-section {
      .scan-btn {
        height: 72px;
        border-radius: 36px;
        font-size: 20px;
        margin-bottom: 20px;
      }

      .scan-tips {
        font-size: 16px;

        .van-icon {
          font-size: 18px;
        }
      }
    }
  }

  .scanner-overlay {
    .scan-frame {
      width: 400px;
      height: 400px;
      margin-bottom: 40px;

      .frame-corner {
        width: 40px;
        height: 40px;
        border-width: 4px;
      }
    }

    .scan-guide {
      font-size: 18px;
    }
  }

  .scanner-actions {
    padding: 32px;

    .close-scanner-btn {
      height: 60px;
      border-radius: 30px;
      font-size: 18px;
      padding: 0 40px;
    }
  }

  .result-section {
    .status-card {
      padding: 40px;
      margin-bottom: 32px;
      gap: 32px;

      .status-icon .van-icon {
        font-size: 56px;
      }

      .status-content {
        .status-title {
          font-size: 28px;
          margin-bottom: 16px;
        }

        .status-message {
          font-size: 18px;
        }
      }
    }

    .scan-info-card,
    .commodity-card {
      padding: 40px;
      margin-bottom: 32px;

      .card-title {
        font-size: 24px;
        margin-bottom: 32px;
      }
    }

    .info-grid {
      gap: 24px;

      .info-item {
        padding: 20px 0;

        label {
          font-size: 18px;
        }

        span {
          font-size: 18px;

          &.scan-time {
            font-size: 17px;
          }
        }
      }
    }

    .commodity-content {
      gap: 32px;

      .commodity-image {
        width: 200px;
        height: 200px;
        border-radius: 16px;
      }

      .commodity-details {
        .commodity-name {
          font-size: 24px;
          margin-bottom: 24px;
        }

        .commodity-specs {
          gap: 20px;

          .spec-item {
            .spec-icon {
              font-size: 24px;
              width: 28px;
            }

            .spec-content {
              label, span {
                font-size: 16px;
              }
            }
          }
        }
      }
    }

    .action-buttons {
      gap: 20px;

      .action-btn {
        height: 72px;
        border-radius: 36px;
        font-size: 20px;

        .van-icon {
          margin-right: 12px;
        }
      }
    }
  }

  .instruction-section .instruction-card {
    padding: 40px;

    .instruction-title {
      font-size: 28px;
      margin-bottom: 40px;
    }

    .instruction-steps {
      gap: 32px;
    }

    .step-item {
      gap: 24px;

      .step-number {
        width: 48px;
        height: 48px;
        font-size: 20px;
      }

      .step-content {
        h4 {
          font-size: 20px;
        }

        p {
          font-size: 18px;
        }
      }
    }
  }

  .empty-state {
    margin-top: 120px;
  }

  .verifying-overlay .overlay-content {
    :deep(.van-loading__text) {
      font-size: 20px;
      margin-top: 20px;
    }
  }
}

/* 平板设备适配 */
@media (max-width: 1199px) and (min-width: 768px) {
  .verification-content {
    max-width: 600px;
  }
}

/* 移动端适配 */
@media (max-width: 767px) {
  .verification-content {
    padding: 16px;
  }

  .input-section {
    padding: 20px;
    margin-bottom: 16px;
  }

  .commodity-content {
    flex-direction: column;

    .commodity-image {
      width: 100%;
      height: 200px;
      margin-bottom: 16px;
    }
  }

  .action-buttons {
    flex-direction: column;
  }
}

/* 超小屏幕设备 */
@media (max-width: 480px) {
  .verification-content {
    padding: 12px;
  }

  .input-section {
    padding: 16px;
  }

  .result-section {
    .status-card,
    .scan-info-card,
    .commodity-card {
      padding: 20px;
    }
  }
}

/* 横屏模式适配 */
@media (max-height: 500px) and (orientation: landscape) {
  .scanner-overlay .scan-frame {
    width: 250px;
    height: 250px;
  }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
  .verification-page {
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  }

  .input-section,
  .status-card,
  .scan-info-card,
  .commodity-card,
  .instruction-card {
    background: #2a2a2a;
  }

  .section-title,
  .status-title,
  .card-title,
  .commodity-name,
  .instruction-title,
  .step-content h4 {
    color: #ffffff;
  }

  .status-message,
  .scan-tips,
  .info-item label,
  .spec-content label,
  .step-content p {
    color: #cccccc;
  }

  .info-item span,
  .spec-content span {
    color: #ffffff;
  }

  .action-buttons .secondary-btn {
    background: #333333;
    border-color: #555555;
    color: #cccccc;
  }
}
</style>
